import { useEffect, useState } from "react";
import { useFetchAllJobTasks } from "@labeling-tools/api/lebron/react-query-hooks";
import { JobTaskStatus } from "@augmodo/lebron-browser-client";
import { getCurrentUser } from "@/utils/auth";
import { COVERAGE_BETA_TASK_TYPE } from "@/utils/profile-metrics";

export interface RejectedQaTask {
  id: string;
  job_id: string;
  created_at: string;
  result?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
}

/** Which QA tasks a profile's QA Review section lists, per task type. */
export interface QaReviewConfig {
  /** Lebron QA task type. */
  qaType: string;
  /** Filters on top of "flagged and not yet reviewed" for Needs Review. */
  needsReviewFilter: Record<string, unknown>;
  /** Filters for the history tab. */
  historyFilter: Record<string, unknown>;
  historyLabel: string;
  historyEmptyLabel: string;
  /** Header for the QA task's created-at column. */
  dateColumnLabel: string;
  /** Route segment of the labeler-facing feedback view. */
  feedbackRoute: string;
  /** Short name shown in Needs Review, which lists every QA type together. */
  typeLabel: string;
}

/** A QA task together with the config of the QA type it belongs to. */
export interface QaTaskRow {
  task: RejectedQaTask;
  config: QaReviewConfig;
}

export const OOS_QA_REVIEW: QaReviewConfig = {
  typeLabel: "OOS",
  qaType: "OUT_OF_STOCK_CONFIRMATION_QA",
  needsReviewFilter: { qa_confirmation_correct: false },
  historyFilter: { qa_confirmation_correct: false },
  historyLabel: "All Rejected",
  historyEmptyLabel: "No rejected tasks",
  dateColumnLabel: "Rejected On",
  feedbackRoute: "confirmation-qa-feedback",
};

// Coverage Beta QA results have no overall correct/incorrect verdict, so the
// history tab lists every task the reviewer flagged for the labeler instead.
export const COVERAGE_BETA_QA_REVIEW: QaReviewConfig = {
  typeLabel: "Coverage Beta",
  qaType: "COVERAGE_REVIEW_BETA_QA",
  needsReviewFilter: {},
  historyFilter: { flag_labeler: true },
  historyLabel: "All Flagged",
  historyEmptyLabel: "No flagged tasks",
  dateColumnLabel: "Flagged On",
  feedbackRoute: "coverage-beta-qa-feedback",
};

/** Coverage Beta gets its own QA list; every other profile type shows OOS QA. */
export function qaReviewConfigFor(taskType: string): QaReviewConfig {
  return taskType === COVERAGE_BETA_TASK_TYPE
    ? COVERAGE_BETA_QA_REVIEW
    : OOS_QA_REVIEW;
}

/**
 * Email of the labeler whose QA'd work to list: the signed-in user for "me"
 * (once Firebase auth resolves), otherwise the `assignee` email itself. Null
 * until known, which keeps the queries disabled. Lowercased, since lebron
 * stores `source_task_user` as the lowercased email.
 */
function useSourceTaskUser(assignee: string): string | null {
  const [myEmail, setMyEmail] = useState<string | null>(null);

  useEffect(() => {
    getCurrentUser().then((user) => {
      if (user?.uid) setMyEmail(user.email ?? "");
    });
  }, []);

  return assignee === "me"
    ? (myEmail?.toLowerCase() ?? null)
    : assignee.toLowerCase();
}

// lebron's `assignee` filter also matches tasks the user only reviewed as QA
// (assigned_to_user), so `source_task_user` narrows to the labeler whose work
// was QA'd. Filter client-side too, in case lebron doesn't support it yet.
function ownTasks(
  tasks: unknown[] | undefined,
  sourceTaskUser: string | null,
): RejectedQaTask[] {
  return ((tasks ?? []) as RejectedQaTask[]).filter(
    (t) =>
      (t.metadata?.source_task_user as string | undefined)?.toLowerCase() ===
      sourceTaskUser,
  );
}

/**
 * QA tasks flagged for labeler review that the labeler hasn't opened yet
 * (labeler_reviewed_at is null). Filtering is done on the backend so
 * pagination and badge counts are accurate.
 */
export function useMyRejectedQaTasks(
  config: QaReviewConfig,
  assignee = "me",
  pageSize?: number,
) {
  const sourceTaskUser = useSourceTaskUser(assignee);

  const query = useFetchAllJobTasks(
    sourceTaskUser !== null
      ? {
          assignee,
          source_task_user: sourceTaskUser,
          type: config.qaType,
          status: JobTaskStatus.COMPLETED,
          ordering: "-created_at",
          flag_labeler: true,
          labeler_reviewed_at_isnull: true,
          ...config.needsReviewFilter,
          ...(pageSize ? { page_size: pageSize } : {}),
        }
      : null,
  );

  const needsReview = ownTasks(query.data?.tasks, sourceTaskUser);
  const needsReviewCount: number = query.data?.count ?? 0;

  return { ...query, needsReview, needsReviewCount };
}

/**
 * Needs Review across every QA type, newest first. "What do I still need to
 * look at" isn't a per-task-type question, so this backs both the header badge
 * and the profile's Needs Review tab, whichever task type the profile shows.
 */
export function useNeedsReview(assignee = "me", pageSize?: number) {
  const oos = useMyRejectedQaTasks(OOS_QA_REVIEW, assignee, pageSize);
  const coverageBeta = useMyRejectedQaTasks(
    COVERAGE_BETA_QA_REVIEW,
    assignee,
    pageSize,
  );

  const rows: QaTaskRow[] = [
    ...oos.needsReview.map((task) => ({ task, config: OOS_QA_REVIEW })),
    ...coverageBeta.needsReview.map((task) => ({
      task,
      config: COVERAGE_BETA_QA_REVIEW,
    })),
  ].sort((a, b) => b.task.created_at.localeCompare(a.task.created_at));

  return {
    rows,
    count: oos.needsReviewCount + coverageBeta.needsReviewCount,
    isLoading: oos.isLoading || coverageBeta.isLoading,
    isError: oos.isError || coverageBeta.isError,
  };
}

/** Paginated query for the history tab — separate from the badge/Needs-Review source. */
export function useAllRejectedQaTasks(
  config: QaReviewConfig,
  page: number,
  assignee = "me",
) {
  const sourceTaskUser = useSourceTaskUser(assignee);

  const query = useFetchAllJobTasks(
    sourceTaskUser !== null
      ? {
          assignee,
          source_task_user: sourceTaskUser,
          type: config.qaType,
          status: JobTaskStatus.COMPLETED,
          ordering: "-created_at",
          ...config.historyFilter,
          page,
        }
      : null,
  );

  return { ...query, tasks: ownTasks(query.data?.tasks, sourceTaskUser) };
}
